import React, { FormEvent, useState } from 'react';
import { FormikProps } from 'formik';
import {
  Form,
  TextFieldControl,
  ModalView,
  Stack,
  Trans,
  Text,
  withTranslation,
  ButtonAppearance,
} from '@piwikpro/ui-components';
import { withInject } from '@piwikpro/platform';
import { FormValidator } from '@piwikpro/form-crate';
import { regExps } from '@piwikpro/form-crate/validators/regExps';

interface InjectedProps {
  i18n: any
  validation: FormValidator
}

export interface ConfirmWithCheckProps extends InjectedProps {
  messages: Array<string>
  name: string
  title: string
  applyBtnAppearance?: string
  applyBtnText?: string
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onCancel: () => void
  onError?: (error: unknown) => void
  asyncAction?: () => Promise<void>
  t: (key: string, variables?: any) => string
}

export const ConfirmWithCheck: React.FC<ConfirmWithCheckProps> = ({
  validation,
  i18n,
  name,
  messages,
  title,
  onCancel,
  t,
  applyBtnAppearance,
  applyBtnText,
  onSubmit,
  onError = () => {},
  asyncAction,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = () => validation.createValidationSchema(() => ({
    name: validation.getYup().string()
      .required()
      // this value may include special characters,
      // which we want to exclude from regexp during matching values
      .matches(new RegExp(`^${name.replace(regExps.specialCharactersToReplace, '\\$&')}$`, 'g'), i18n.t('administration:deleteConfirm.withCheck.notMatch')),
  }));

  const handleSubmit = async (values: any, formikApi: FormikProps<{ name: string }>) => {
    if (asyncAction) {
      setIsSubmitting(true);
      try {
        await asyncAction();
        setIsSubmitting(false);
        onSubmit(values);
      } catch (error) {
        // Modal stays open on error, hide loader and propagate error
        setIsSubmitting(false);
        formikApi.setSubmitting(false);
        onError(error);
      }
    } else {
      onSubmit(values);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onCancel();
    }
  };

  return (
    <Form
      id="confirm-with-check-form"
      validationSchema={validationSchema}
      initialValues={{
        name: '',
      }}
      onSubmit={handleSubmit}
    >
      <ModalView
        title={t(title, { name })}
        actions={(
          <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
            <Form.Cancel onClick={onCancel} disabled={isSubmitting} />
            <Form.Submit
              appearance={(applyBtnAppearance || 'default') as ButtonAppearance}
              text={applyBtnText ? t(applyBtnText) : t('administration:deleteConfirm.modal.submit-confirm')}
            />
          </Stack>
        )}
        onClose={handleClose}
      >
        <Stack vertical horizontalAlignment="fill" spacing="wide">
          <Stack.Item>
            <Stack vertical spacing="narrow">
              {messages?.map((message: string) => (
                <Stack.Item key={message}>
                  <Text>
                    <Trans
                      i18nKey={t(message, { name })}
                      components={[
                        <b>text</b>,
                        <b>text</b>,
                        <b>text</b>,
                      ]}
                    />
                  </Text>
                </Stack.Item>
              ))}
            </Stack>
          </Stack.Item>
          <Stack.Item previousSpacing="narrow">
            <TextFieldControl
              name="name"
              required
              autoFocus
              disabled={isSubmitting}
            />
          </Stack.Item>
        </Stack>
      </ModalView>
    </Form>
  );
};

export default withInject<InjectedProps>({
  i18n: 'TranslationCrate.i18n',
  validation: 'FormCrate.validation',
})(withTranslation(['administration', 'platform'])(ConfirmWithCheck));
